import { useContext, useState } from 'react';
import { Client, Context } from 'urql';
import {
  GetBrokerBranchInfoDocument,
  GetBrokerBranchInfoQuery,
  GetBrokerInfoDocument,
  GetBrokerInfoQuery,
} from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';

export type CodeNameMap = {
  code: string;
  name: string;
};

/**
 * 金融機関名検索
 * @param onDataFetched
 * @returns
 */
export const useBrokerInfoSearch = (onDataFetched: (data: CodeNameMap[]) => void) => {
  const useClient = () => useContext(Context);
  const client = useClient() as Client;
  // 検索結果無い場合のエラー表示用
  const [isGojuonNoResult, setIsGojuonNoResult] = useState(false);
  const [isKanaNoResult, setIsKanaNoResult] = useState(false);
  const [isCodeNoResult, setIsCodeNoResult] = useState(false);

  // 五十音検索
  const getBrokerInfoByGojuon = useOnceSubmit<string>(
    {
      onSubmit: async (brokerName) => {
        try {
          initNoResult();
          const ret = await client.query(GetBrokerInfoDocument, { brokerName }, { requestPolicy: 'network-only' });
          const data = createBrokerInfoList(ret.data);
          if (data.length === 0) {
            setIsGojuonNoResult(true);
            return;
          }
          onDataFetched(data);
        } catch {
          setIsGojuonNoResult(true);
        }
      },
    },
    [client],
  );

  // カナ検索
  const getBrokerInfoByKana = useOnceSubmit<string>(
    {
      onSubmit: async (brokerName) => {
        try {
          initNoResult();
          const ret = await client.query(GetBrokerInfoDocument, { brokerName }, { requestPolicy: 'network-only' });
          const data = createBrokerInfoList(ret.data);
          if (data.length === 0) {
            setIsKanaNoResult(true);
            return;
          }

          onDataFetched(data);
        } catch {
          setIsKanaNoResult(true);
        }
      },
    },
    [client],
  );

  // 金融機関コード検索
  const getBrokerInfoByCode = useOnceSubmit<string>(
    {
      onSubmit: async (brokerCode) => {
        try {
          initNoResult();
          const ret = await client.query(GetBrokerInfoDocument, { brokerCode }, { requestPolicy: 'network-only' });
          const data = createBrokerInfoList(ret.data);
          if (data.length === 0) {
            setIsCodeNoResult(true);
            return;
          }
          onDataFetched(data);
        } catch {
          setIsCodeNoResult(true);
        }
      },
    },
    [client],
  );

  // graphqlの結果から変換
  function createBrokerInfoList(data: GetBrokerInfoQuery): CodeNameMap[] {
    if (!data.GetBrokerInfo?.Data) return [];

    return data.GetBrokerInfo.Data.map((item) => ({ code: item?.BrokerCode, name: item?.BrokerName })).filter(
      (item): item is { code: string; name: string } => !!item.code && !!item.name,
    );
  }

  // エラー表示初期化
  function initNoResult() {
    setIsGojuonNoResult(false);
    setIsKanaNoResult(false);
    setIsCodeNoResult(false);
  }

  return {
    searchGojuon: { getBrokerInfoByGojuon, isGojuonNoResult },
    searchKana: { getBrokerInfoByKana, isKanaNoResult },
    searchCode: { getBrokerInfoByCode, isCodeNoResult },
  };
};

/**
 * 支店名検索
 * @param onDataFetched
 * @returns
 */
export const useBranchInfoSearch = (selectedBrokerCode: string, onDataFetched: (data: CodeNameMap[]) => void) => {
  const useClient = () => useContext(Context);
  const client = useClient() as Client;

  // 検索結果無い場合のエラー表示用
  const [isGojuonNoResult, setIsGojuonNoResult] = useState(false);
  const [isKanaNoResult, setIsKanaNoResult] = useState(false);
  const [isCodeNoResult, setIsCodeNoResult] = useState(false);

  // 支店名五十音検索
  const getBranchInfoByGojuon = useOnceSubmit<string>(
    {
      onSubmit: async (branchName) => {
        try {
          initNoResult();
          const ret = await client.query(
            GetBrokerBranchInfoDocument,
            { brokerCode: selectedBrokerCode, branchName },
            { requestPolicy: 'network-only' },
          );
          const data = createBrokerBranchInfoList(ret.data);
          if (data.length === 0) {
            setIsGojuonNoResult(true);
            return;
          }
          onDataFetched(data);
        } catch {
          setIsGojuonNoResult(true);
        }
      },
    },
    [client],
  );

  // 支店名カナ検索
  const getBranchInfoByKana = useOnceSubmit<string>(
    {
      onSubmit: async (branchName) => {
        try {
          initNoResult();
          const ret = await client.query(
            GetBrokerBranchInfoDocument,
            { brokerCode: selectedBrokerCode, branchName },
            { requestPolicy: 'network-only' },
          );
          const data = createBrokerBranchInfoList(ret.data);
          if (data.length === 0) {
            setIsKanaNoResult(true);
            return;
          }
          onDataFetched(data);
        } catch {
          setIsKanaNoResult(true);
        }
      },
    },
    [client],
  );

  // 支店コード検索
  const getBranchInfoByCode = useOnceSubmit<string>(
    {
      onSubmit: async (branchCode) => {
        try {
          initNoResult();
          const ret = await client.query(
            GetBrokerBranchInfoDocument,
            { brokerCode: selectedBrokerCode, branchCode },
            { requestPolicy: 'network-only' },
          );
          const data = createBrokerBranchInfoList(ret.data);
          if (data.length === 0) {
            setIsCodeNoResult(true);
            return;
          }
          onDataFetched(data);
        } catch {
          setIsCodeNoResult(true);
        }
      },
    },
    [client],
  );

  // graphqlの結果から変換
  function createBrokerBranchInfoList(data: GetBrokerBranchInfoQuery): { code: string; name: string }[] {
    if (!data.GetBrokerBranchInfo?.Data) return [];
    return data.GetBrokerBranchInfo?.Data.map((item) => ({ code: item?.BranchCode, name: item?.BranchName })).filter(
      (item): item is { code: string; name: string } => !!item.code && !!item.name,
    );
  }

  // エラー表示初期化
  function initNoResult() {
    setIsGojuonNoResult(false);
    setIsKanaNoResult(false);
    setIsCodeNoResult(false);
  }

  // return {
  //   getBranchInfoByKana,
  //   getBranchInfoByCode,
  // };

  return {
    searchGojuon: { getBranchInfoByGojuon, isGojuonNoResult },
    searchKana: { getBranchInfoByKana, isKanaNoResult },
    searchCode: { getBranchInfoByCode, isCodeNoResult },
  };
};
